// ~/util/bibliotecaDeFunciones.js
export default class BibliotecaDeFunciones {
  /**
   * Devuelve todas las funciones disponibles en la biblioteca.
   * Esta es la que usarás normalmente en el editor.
   */
  static obtenerFunciones() {
    return [
      this._definirConcatenar(),
      this._definirImprimeSiContiene(),
      this._definirMayusculas(),
      this._definirConcatenarArray(),
      this._definirConcatenarArraySi(),
      this._definirExisteEnArray(),
      this._definirBuscaEnArray(),
      this._definirFechaMostrar(),
      this._definirTabla(),
      this._definirCelda(),
    ]
  }

  /**
   * Devuelve la definición de una función concreta por su nombre,
   * por ejemplo: "imprimePosicion", "fechaCalcula", etc.
   */
  static obtenerFuncionPorNombre(nombreFuncion) {
    return (
      this.obtenerFunciones().find((definicion) => definicion.nombreFuncion === nombreFuncion) ||
      null
    )
  }

  /**
   * Texto de ayuda general sobre la sintaxis y el uso de funciones/variables.
   * Ideal para mostrar en un dialog de "Ayuda".
   */
  static obtenerAyudaSintaxisGeneral() {
    return {
      titulo: 'Ayuda de sintaxis de variables y funciones',
      descripcion:
        'Esta sección explica cómo escribir variables, alias y funciones dentro de las plantillas usando el formato ${...}.',
      secciones: [
        {
          titulo: '1. Marcadores básicos',
          contenido: [
            'Todas las expresiones se escriben entre ${ y }.',
            'Ejemplo simple: ${v:solicitud.user.nombre}',
            'Dentro de ${...} puedes usar variables, alias y funciones.',
          ],
        },
        {
          titulo: '2. Acceso a datos (prefijos de variables)',
          contenido: [
            'v:ruta       → Inserta un valor de texto usando la ruta completa (fullPath).',
            'i:ruta       → Inserta una imagen base64 usando la ruta completa (fullPath).',
          ],
          ejemplos: [
            '${v:solicitud.user.nombre}',
            '${i:solicitud.user.empresa.logotipo}',
          ],
        },
        {
          titulo: '3. Funciones',
          contenido: [
            'Las funciones se escriben como: ${nombreFuncion:arg1,arg2,...}',
            'Cada argumento puede ser:',
            '- Un literal de texto entre comillas: "Hola mundo"',
            '- Un número: 0, 3, 25.5',
            '- Una ruta/alias: v:solicitud.user.nombre, nombreOperador',
          ],
          ejemplos: [
            '${concatenar:",",v:solicitud.user.nombre,v:solicitud.user.apellido1}',
          ],
        },
        {
          titulo: '4. Combinación de funciones',
          contenido: [
            'Algunas funciones admiten como argumento el resultado de otra función.',
            'Sintaxis obligatoria: ${funcionExterna:(funcionInterna:...)}',
            'Los paréntesis hacen que la función interna se trate como un solo argumento.',
            'No uses llaves { } ni omitas los paréntesis en funciones anidadas.',
          ],
          ejemplos: [
            '${mayusculas:(concatenar:"  ",nombreN1,nombreN2)}',
          ],
        },
        {
          titulo: '5. Alias',
          contenido: [
            'Los alias se definen en el editor de variables.',
            'Es recomendable usar alias en lugar de rutas largas, sobre todo si la ruta contiene índices (0,1,2,...) o es muy verbosa.',
            'Si cambia la estructura interna del JSON, solo tendrás que actualizar el alias.',
          ],
          ejemplos: [
            'En lugar de ${v:solicitud.productos.0.params.0.valor.0}',
            'puedes definir un alias "primerProducto" y usar ${primerProducto}.',
          ],
        },
      ],
    }
  }

  // ---------------------------------------------------------------------------
  // Definiciones individuales (privadas) de cada función
  // ---------------------------------------------------------------------------

  static _definirConcatenar() {
    return {
      nombreFuncion: 'concatenar',
      etiqueta: 'Concatenar',
      categoria: 'Texto',
      descripcion:
        'Concatena múltiples valores usando un separador. Ignora valores nulos o vacíos.',
      sintaxis: '${concatenar:"separador",valor1,valor2,...}',
      plantillaExpresion: '${concatenar:" ",CAMPO1,CAMPO2}',
      ejemplo: '${concatenar:" ",v:nodo.nombre,v:nodo.apellidoPaterno}  // "Jorge Cardenas"',

      argumentos: [
        {
          nombre: 'separador',
          etiqueta: 'Separador',
          tipo: 'string',
          requerido: true,
          descripcion: 'Texto literal entre comillas dobles usado para separar los valores.',
          ejemplo: '", "',
        },
        {
          nombre: 'valores',
          etiqueta: 'Valores a concatenar',
          tipo: 'variadic',
          requerido: true,
          descripcion:
            'Lista de valores (variables, alias o literales) que se unirán con el separador.',
          ejemplo: 'v:nodo.nombre,v:nodo.apellidoPaterno',
        },
      ],

      infoTecnica: {
        formula: '${concatenar:"separador",valor1,valor2,...}',
        parametros: [
          {
            nombre: 'separador',
            requerido: true,
            exacto: false,
            descripcion:
              'String literal (recomendado) o valor que se usará como separador. Si es null/undefined => "".',
          },
          {
            nombre: 'valores',
            requerido: true,
            exacto: false,
            descripcion:
              'Valores variádicos a unir. Se ignoran null/undefined y "" (string vacío).',
          },
        ],
        procesamiento: [
          'Normaliza el separador a string (null/undefined => "").',
          'Filtra valores null/undefined y "".',
          'Convierte cada valor restante a string.',
          'Une con el separador.',
        ],
        retorno: 'string con los valores concatenados.',
        ejemplo: '${concatenar:",","A","","0","B"} => "A,0,B"',
        usoEnOtraFuncion: '${mayusculas:(concatenar:" ",v:nombre,v:apellido)}',
      },

      tipoRetorno: 'string',
    }
  }

  static _definirImprimeSiContiene() {
    return {
      nombreFuncion: 'imprimeSiContiene',
      etiqueta: 'Imprime si contiene',
      categoria: 'Texto',
      descripcion:
        'Busca un texto dentro de otro y, si lo encuentra, imprime un valor de salida.',
      sintaxis: '${imprimeSiContiene:"buscar",texto,resultado}',
      plantillaExpresion: '${imprimeSiContiene:" ALMACÉN",CAMPO,"X"}',
      ejemplo: '${imprimeSiContiene:" ALMACÉN",v:estatico.0.texto,"X"}',

      argumentos: [
        {
          nombre: 'buscar',
          etiqueta: 'Texto a buscar',
          tipo: 'string',
          requerido: true,
          descripcion: 'Texto literal entre comillas dobles que se buscará dentro del argumento 2.',
          ejemplo: '" ALMACÉN"',
        },
        {
          nombre: 'texto',
          etiqueta: 'Texto completo',
          tipo: 'string|path|funcion',
          requerido: true,
          descripcion: 'Texto donde se realizará la búsqueda.',
          ejemplo: 'v:estatico.0.texto',
        },
        {
          nombre: 'resultado',
          etiqueta: 'Salida',
          tipo: 'string|path|funcion',
          requerido: true,
          descripcion:
            'Texto o valor que se imprimirá si el argumento 1 se encuentra en el argumento 2.',
          ejemplo: '"X"',
        },
      ],

      infoTecnica: {
        formula: '${imprimeSiContiene:"buscar",texto,resultado}',
        parametros: [
          {
            nombre: 'buscar',
            requerido: true,
            exacto: false,
            descripcion:
              'Texto a buscar (match parcial, case-insensitive). Recomendado literal entre comillas.',
          },
          {
            nombre: 'texto',
            requerido: true,
            exacto: false,
            descripcion: 'Texto donde se buscará; si es array se concatena con espacios.',
          },
          {
            nombre: 'resultado',
            requerido: true,
            exacto: false,
            descripcion: 'Valor a imprimir si hay match; se convierte a string.',
          },
        ],
        procesamiento: [
          'Convierte `buscar` y `texto` a string y a minúsculas.',
          'Si `texto` es array, lo concatena con espacios.',
          'Si `buscar` está vacío, retorna "".',
          'Si hay match parcial, retorna String(resultado); si no, "".',
        ],
        retorno: 'string con el resultado o "".',
        ejemplo:
          '${imprimeSiContiene:"almacen",v:zonas[*].nombre,"X"}  // "X" si contiene',
        usoEnOtraFuncion:
          '${concatenar:" ",v:zona,(imprimeSiContiene:"ALM",v:zona,"X")}',
      },

      tipoRetorno: 'string',
    }
  }

  static _definirMayusculas() {
    return {
      nombreFuncion: 'mayusculas',
      etiqueta: 'Mayúsculas',
      categoria: 'Texto',
      descripcion: 'Convierte el texto de origen a mayúsculas.',
      sintaxis: '${mayusculas:texto}',
      plantillaExpresion: '${mayusculas:CAMPO}',
      ejemplo: '${mayusculas:v:estatico.0.texto}',

      argumentos: [
        {
          nombre: 'texto',
          etiqueta: 'Texto',
          tipo: 'string|path|funcion',
          requerido: true,
          descripcion: 'Texto o ruta que se desea convertir a mayúsculas.',
          ejemplo: 'v:estatico.0.texto',
        },
      ],

      infoTecnica: {
        formula: '${mayusculas:texto}',
        parametros: [
          {
            nombre: 'texto',
            requerido: true,
            exacto: false,
            descripcion: 'Texto o ruta a convertir; si es array se concatena con espacios.',
          },
        ],
        procesamiento: [
          'Si `texto` es array, concatena con espacios ignorando null/"".',
          'Convierte a string y aplica `toUpperCase()` (locale runtime).',
          'Si `texto` es null/undefined, retorna "".',
        ],
        retorno: 'string en mayúsculas.',
        ejemplo: '${mayusculas:"hola mundo"} => "HOLA MUNDO"',
        usoEnOtraFuncion: '${mayusculas:(concatenar:" ",v:nombre,v:apellido)}',
      },

      tipoRetorno: 'string',
    }
  }

  static _definirConcatenarArray() {
    return {
      nombreFuncion: 'concatenarArray',
      etiqueta: 'Concatenar array',
      categoria: 'Texto',
      descripcion:
        'Concatena valores de un array usando un separador. Soporta rutas con [*].',
      sintaxis: '${concatenarArray:array,"separador"}',
      plantillaExpresion: '${concatenarArray:nodo[*].campo,", "}',
      ejemplo: '${concatenarArray:v:nodo[*].campo,", "}',

      argumentos: [
        {
          nombre: 'array',
          etiqueta: 'Array de valores',
          tipo: 'array|path',
          requerido: true,
          descripcion: 'Ruta a un array o wildcard [*] que devuelve múltiples valores.',
          ejemplo: 'v:nodo[*].campo',
        },
        {
          nombre: 'separador',
          etiqueta: 'Separador',
          tipo: 'string',
          requerido: true,
          descripcion: 'Texto literal entre comillas dobles usado para separar.',
          ejemplo: '", "',
        },
      ],

      infoTecnica: {
        formula: '${concatenarArray:array,"separador"}',
        parametros: [
          {
            nombre: 'array',
            requerido: true,
            exacto: false,
            descripcion: 'Array (o valor único) con elementos a concatenar.',
          },
          {
            nombre: 'separador',
            requerido: true,
            exacto: false,
            descripcion:
              'Separador literal (recomendado). Si es null/undefined se usa "".',
          },
        ],
        procesamiento: [
          'Normaliza a array (si no lo es, envuelve).',
          'Filtra null/"" y convierte cada item a string.',
          'Une con el separador.',
        ],
        retorno: 'string con los valores concatenados.',
        ejemplo: '${concatenarArray:v:nodo[*].campo," | "}  // "A | B"',
        usoEnOtraFuncion: '${mayusculas:(concatenarArray:v:nodo[*].campo,", ")}',
      },

      tipoRetorno: 'string',
    }
  }

  static _definirConcatenarArraySi() {
    return {
      nombreFuncion: 'concatenarArraySi',
      etiqueta: 'Concatenar array si contiene',
      categoria: 'Texto',
      descripcion:
        'Concatena valores de un array usando un separador solo si contienen un texto.',
      sintaxis: '${concatenarArraySi:array,"separador","buscar"}',
      plantillaExpresion: '${concatenarArraySi:nodo[*].campo,", ","ALMACÉN"}',
      ejemplo: '${concatenarArraySi:v:nodo[*].campo,", ","ALMACÉN"}',

      argumentos: [
        {
          nombre: 'array',
          etiqueta: 'Array de valores',
          tipo: 'array|path',
          requerido: true,
          descripcion: 'Ruta a un array o wildcard [*] que devuelve múltiples valores.',
          ejemplo: 'v:nodo[*].campo',
        },
        {
          nombre: 'separador',
          etiqueta: 'Separador',
          tipo: 'string',
          requerido: true,
          descripcion: 'Texto literal entre comillas dobles usado para separar.',
          ejemplo: '", "',
        },
        {
          nombre: 'buscar',
          etiqueta: 'Texto a buscar',
          tipo: 'string',
          requerido: true,
          descripcion: 'Texto literal a buscar dentro de cada valor del array.',
          ejemplo: '"ALMACÉN"',
        },
      ],

      infoTecnica: {
        formula: '${concatenarArraySi:array,"separador","buscar"}',
        parametros: [
          {
            nombre: 'array',
            requerido: true,
            exacto: false,
            descripcion: 'Array (o valor único) con elementos a evaluar.',
          },
          {
            nombre: 'separador',
            requerido: true,
            exacto: false,
            descripcion: 'Separador literal (recomendado).',
          },
          {
            nombre: 'buscar',
            requerido: true,
            exacto: false,
            descripcion: 'Texto a buscar con `includes` (case-sensitive).',
          },
        ],
        procesamiento: [
          'Normaliza a array; filtra null/"" y convierte a string.',
          'Si `buscar` es vacío, no filtra por contenido.',
          'Si `buscar` tiene valor, filtra por `includes` (case-sensitive).',
          'Une con el separador.',
        ],
        retorno: 'string con los valores concatenados que cumplen el filtro.',
        ejemplo: '${concatenarArraySi:v:items[*].nombre,", ","AL"}  // "ALMACEN"',
        usoEnOtraFuncion:
          '${mayusculas:(concatenarArraySi:v:items[*].nombre,", ","ALM")}',
      },

      tipoRetorno: 'string',
    }
  }

  static _definirExisteEnArray() {
    return {
      nombreFuncion: 'existeEnArray',
      etiqueta: 'Existe en array',
      categoria: 'Texto',
      descripcion:
        'Devuelve "1" si el valor buscado existe dentro del array. Si no existe, devuelve "0".',
      sintaxis: '${existeEnArray:array,"buscar"}',
      plantillaExpresion: '${existeEnArray:nodo,"valorX"}',
      ejemplo: '${existeEnArray:v:nodo,"valor2"}',

      argumentos: [
        {
          nombre: 'array',
          etiqueta: 'Array de valores',
          tipo: 'array|path',
          requerido: true,
          descripcion:
            'Ruta a un array/objeto de valores (p. ej. selecciones del usuario). También acepta wildcard [*].',
          ejemplo: 'v:nodo',
        },
        {
          nombre: 'buscar',
          etiqueta: 'Valor a buscar',
          tipo: 'string|path|funcion',
          requerido: true,
          descripcion: 'Valor exacto a buscar dentro del array.',
          ejemplo: '"valor2"',
        },
      ],

      infoTecnica: {
        formula: '${existeEnArray:array,"buscar"}',
        parametros: [
          {
            nombre: 'array',
            requerido: true,
            exacto: false,
            descripcion: 'Array, objeto o valor simple; objetos usan Object.values.',
          },
          {
            nombre: 'buscar',
            requerido: true,
            exacto: true,
            descripcion: 'Valor a comparar con igualdad exacta (String).',
          },
        ],
        procesamiento: [
          'Normaliza `array` a lista de valores.',
          'Compara `String(valor) === String(buscar)`.',
          'Si hay match, retorna "1"; si no, "0".',
        ],
        retorno: 'string "1" o "0".',
        ejemplo: '${existeEnArray:v:lista,"2"}  // "1" si existe "2"',
        usoEnOtraFuncion:
          '${imprimeSiContiene:"1",(existeEnArray:v:lista,"X"),"SI"}',
      },

      tipoRetorno: 'string',
    }
  }

  static _definirBuscaEnArray() {
    return {
      nombreFuncion: 'buscaEnArray',
      etiqueta: 'Busca en array',
      categoria: 'Texto',

      descripcion:
        'Busca un valor dentro de un array y devuelve el campo relacionado en la misma posición.',
      sintaxis: '${buscaEnArray:valor,campos,resultado}',
      plantillaExpresion:
        '${buscaEnArray:"ID-123",user.direcciones[*].uuid,user.direcciones[*].rgseaa}',
      ejemplo: '${buscaEnArray:v:emplazamiento_id,user.direcciones[*].uuid,user.direcciones[*].rgseaa}',

      argumentos: [
        {
          nombre: 'valor',
          etiqueta: 'Valor a buscar',
          tipo: 'string|path|funcion',
          requerido: true,
          descripcion: 'Valor exacto a buscar dentro del array de campos.',
          ejemplo: '"ID-123"',
        },
        {
          nombre: 'campos',
          etiqueta: 'Campos donde buscar',
          tipo: 'array|path',
          requerido: true,
          descripcion: 'Array de valores donde se buscará el valor.',
          ejemplo: 'user.direcciones[*].uuid',
        },
        {
          nombre: 'resultado',
          etiqueta: 'Campo a imprimir',
          tipo: 'array|path',
          requerido: true,
          descripcion:
            'Array de valores del mismo nodo que se devolverá cuando haya coincidencia.',
          ejemplo: 'user.direcciones[*].rgseaa',
        },
      ],

      infoTecnica: {
        formula: '${buscaEnArray:valor,campos,resultado}',
        parametros: [
          {
            nombre: 'valor',
            requerido: true,
            exacto: true,
            descripcion: 'Valor a buscar con igualdad exacta (String).',
          },
          {
            nombre: 'campos',
            requerido: true,
            exacto: false,
            descripcion: 'Array (o valor/objeto) donde se busca el valor.',
          },
          {
            nombre: 'resultado',
            requerido: true,
            exacto: false,
            descripcion: 'Array (o valor/objeto) con el dato a retornar por índice.',
          },
        ],
        procesamiento: [
          'Normaliza `campos` y `resultado` a arrays; objetos usan Object.values.',
          'Busca índice donde `String(campo) === String(valor)`.',
          'Si no encuentra, retorna "".',
          'Si encuentra, retorna el elemento de `resultado` con el mismo índice.',
        ],
        retorno: 'string con el valor encontrado o "".',
        ejemplo:
          '${buscaEnArray:"ID-2",v:arr[*].uuid,v:arr[*].nombre}  // "B"',
        usoEnOtraFuncion:
          '${mayusculas:(buscaEnArray:v:id,v:arr[*].uuid,v:arr[*].nombre)}',
      },

      tipoRetorno: 'string',
    }
  }

  static _definirFechaMostrar() {
    return {
      nombreFuncion: 'fechaMostrar',
      etiqueta: 'Mostrar fecha',
      categoria: 'Fechas',

      descripcion:
        'Formatea una fecha. Acepta "hoy" o fechas en formatos como YYYY-MM-DD, YYYY-MM-DD HH:mm:ss o DD/MM/YYYY.',
      sintaxis: '${fechaMostrar:fecha,"formato"}',
      plantillaExpresion: '${fechaMostrar:"hoy","dd/mm/yyyy"}',
      ejemplo: '${fechaMostrar:v:auditoria.fecha,"mesLetra"}  // "octubre"',

      argumentos: [
        {
          nombre: 'fecha',
          etiqueta: 'Fecha o "hoy"',
          tipo: 'string|path|funcion',
          requerido: true,
          descripcion:
            'Fecha origen. Usa "hoy" para la fecha del servidor. También acepta rutas que devuelvan fechas.',
          ejemplo: '"hoy"',
        },
        {
          nombre: 'formato',
          etiqueta: 'Formato',
          tipo: 'string',
          requerido: true,
          descripcion:
            'Salida: dd/mm/yy, dd/mm/yyyy, diaLetra, mesLetra, anioLetra, diaNum, mesNum, anioNum.',
          ejemplo: '"dd/mm/yyyy"',
        },
      ],

      infoTecnica: {
        formula: '${fechaMostrar:fecha,"formato"}',
        parametros: [
          {
            nombre: 'fecha',
            requerido: true,
            exacto: false,
            descripcion:
              'Date, timestamp (ms) o string. Acepta "hoy" (con comillas) para la fecha actual.',
          },
          {
            nombre: 'formato',
            requerido: true,
            exacto: true,
            descripcion:
              'Formato exacto: dd/mm/yy, dd/mm/yyyy, diaLetra, mesLetra, anioLetra, diaNum, mesNum, anioNum.',
          },
        ],
        procesamiento: [
          'Si fecha es Date, se usa directamente.',
          'Si es número/string numérico, se interpreta como timestamp (ms).',
          'Si es string: "hoy" => Date() actual (zona local).',
          'Parsea en orden: YYYY-MM-DD[ HH:mm[:ss]], DD/MM/YYYY, DD-MM-YYYY, YYYY/MM/DD; fallback Date(raw).',
          'Valida componentes (fecha imposible => "").',
          'Formato se normaliza a minúsculas sin acentos; default `dd/mm/yyyy`.',
          'Salidas en letra: minúsculas y sin acentos.',
        ],
        retorno: 'string con la fecha formateada o "" si es inválida.',
        ejemplo: '${fechaMostrar:"2026-02-09","dd/mm/yyyy"} => "09/02/2026"',
        usoEnOtraFuncion: '${concatenar:" ",(fechaMostrar:"hoy","dd/mm/yyyy"),v:cliente}',
      },

      tipoRetorno: 'string',
    }
  }

  static _definirTabla() {
    return {
      nombreFuncion: 'tabla',
      etiqueta: 'Tabla (driver)',
      categoria: 'Tablas',

      descripcion:
        'Define el driver de una tabla (iteración de filas). Se usa en aliases, no directamente en el DOCX.',
      sintaxis: '${tabla:nombre,nodoPrincipal,flagVacia}',
      plantillaExpresion: '${tabla:tabla01,filas[*],1}',
      ejemplo: '${tabla:tabla01,filas[*],1}',

      argumentos: [
        {
          nombre: 'nombre',
          etiqueta: 'Identificador',
          tipo: 'string',
          requerido: true,
          descripcion: 'Nombre de referencia que compartirán sus celdas.',
          ejemplo: 'tabla01',
        },
        {
          nombre: 'nodoPrincipal',
          etiqueta: 'Nodo principal',
          tipo: 'array|path',
          requerido: true,
          descripcion:
            'Ruta o alias que devuelve el array base. Determina cuántas filas se generan.',
          ejemplo: 'filas[*]',
        },
        {
          nombre: 'flagVacia',
          etiqueta: 'Fila vacía',
          tipo: 'number',
          requerido: false,
          descripcion:
            'Opcional. Si es 1, se crea 1 fila vacía cuando el array está vacío. Si es 0 o vacío, no se imprime ninguna fila.',
          ejemplo: '1',
        },
      ],

      infoTecnica: {
        formula: '${tabla:nombre,nodoPrincipal,flagVacia}',
        parametros: [
          {
            nombre: 'nombre',
            requerido: true,
            exacto: true,
            descripcion:
              'Identificador que debe coincidir exactamente con el usado en celda().',
          },
          {
            nombre: 'nodoPrincipal',
            requerido: true,
            exacto: false,
            descripcion: 'Ruta/alias que devuelve el array base de filas.',
          },
          {
            nombre: 'flagVacia',
            requerido: false,
            exacto: true,
            descripcion: '1 para generar fila vacía si el array está vacío; 0/"" para no generar.',
          },
        ],
        procesamiento: [
          'Función dummy: devuelve "" en tiempo de ejecución.',
          'Durante preproceso, actúa como driver para expandir filas según el array.',
          'Si flagVacia=1 y array vacío, se crea 1 fila vacía.',
        ],
        retorno: 'string "" (no imprime nada directo).',
        ejemplo: 'tabla01 = tabla:tabla01,filas[*],1',
        usoEnOtraFuncion:
          'No se anida. Se usa junto con celda(): celda:tabla01,instalacion_ubicacion',
      },

      tipoRetorno: 'string',
    }
  }

  static _definirCelda() {
    return {
      nombreFuncion: 'celda',
      etiqueta: 'Celda (tabla)',
      categoria: 'Tablas',

      descripcion:
        'Imprime un valor relativo a la fila actual. Se usa en aliases junto con tabla.',
      sintaxis: '${celda:tabla,campo,expresion}',
      plantillaExpresion: '${celda:tabla01,emplazamiento_id[0],buscaEnArray:@v,user.direcciones[*].uuid,user.direcciones[*].rgseaa}',
      ejemplo: '${celda:tabla01,instalacion_ubicacion}',

      argumentos: [
        {
          nombre: 'tabla',
          etiqueta: 'Tabla',
          tipo: 'string',
          requerido: true,
          descripcion: 'Identificador que debe coincidir con el usado en tabla().',
          ejemplo: 'tabla01',
        },
        {
          nombre: 'campo',
          etiqueta: 'Campo base',
          tipo: 'string|path',
          requerido: true,
          descripcion:
            'Campo relativo al nodo principal. Si no hay expresión, se imprime este valor.',
          ejemplo: 'instalacion_ubicacion',
        },
        {
          nombre: 'expresion',
          etiqueta: 'Expresión',
          tipo: 'funcion',
          requerido: false,
          descripcion:
            'Opcional. Expresión a evaluar. Usa @v como placeholder del valor base del campo.',
          ejemplo: 'buscaEnArray:@v,user.direcciones[*].uuid,user.direcciones[*].rgseaa',
        },
      ],

      infoTecnica: {
        formula: '${celda:tabla,campo,expresion}',
        parametros: [
          {
            nombre: 'tabla',
            requerido: true,
            exacto: true,
            descripcion: 'Debe coincidir con el nombre definido en tabla().',
          },
          {
            nombre: 'campo',
            requerido: true,
            exacto: false,
            descripcion: 'Campo relativo al nodo principal de la tabla.',
          },
          {
            nombre: 'expresion',
            requerido: false,
            exacto: false,
            descripcion:
              'Expresión a evaluar; usa @v como valor base del campo.',
          },
        ],
        procesamiento: [
          'Función dummy: devuelve "" en tiempo de ejecución.',
          'Durante preproceso, se evalúa por fila junto con tabla().',
          'Si no hay expresión, imprime el campo base.',
          'Si hay expresión, reemplaza @v por el valor base y evalúa.',
        ],
        retorno: 'string "" (no imprime nada directo).',
        ejemplo: 'celda01 = celda:tabla01,instalacion_ubicacion',
        usoEnOtraFuncion:
          'Si anidas funciones en `expresion`, usa paréntesis: celda:tabla01,campo,(buscaEnArray:@v,arr[*].id,arr[*].valor)',
      },

      tipoRetorno: 'string',
    }
  }
}
